/**
 * useTranscribe — React hook for browser-side Transcribe streaming.
 *
 * Orchestrates:
 *   1. Fetching a presigned Transcribe URL from GET /transcribe-url
 *   2. getUserMedia for mic access (with echo cancellation, noise suppression, AGC)
 *   3. Starting TranscribeClient (AudioWorklet → WebSocket pipeline)
 *   4. Surfacing partial and final transcript callbacks
 *   5. Auto-renewal: if WebSocket closes unexpectedly while listening, fetch a
 *      fresh presigned URL and reconnect (presigned URLs expire after 5 min)
 *
 * Silence heartbeat is handled inside TranscribeClient (10s interval — Pitfall A).
 * Session WebSocket heartbeat (Pitfall B) is owned by the 02-04 session manager.
 *
 * Security (T-02-36): presigned URL is never logged; only logged on error as hostname.
 * Security (T-02-37): stop() calls stream.getTracks().forEach(t => t.stop()) to
 *   release the mic indicator. useEffect cleanup calls stop() on unmount.
 * Security (T-02-31): getUserMedia permission prompt is the user's consent point.
 *
 * Hook signature:
 *   const { start, stop, partial, final, isListening, error } = useTranscribe({
 *     getIdToken,
 *     restApiUrl,
 *   });
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { TranscribeClient } from '../lib/transcribe-client.js';

export interface UseTranscribeOptions {
  /** Returns the current Cognito ID token for Authorization header */
  getIdToken: () => Promise<string>;
  /** Base URL of the REST API (e.g. https://api.mylifehistory.xyz/prod) */
  restApiUrl: string;
}

export interface UseTranscribeReturn {
  /** Start listening: fetches presigned URL, opens mic, connects Transcribe */
  start: () => Promise<void>;
  /** Stop listening: closes WebSocket, stops mic tracks, cleans up */
  stop: () => Promise<void>;
  /** Current partial transcript (clears when a final result arrives) */
  partial: string;
  /** Most recent final transcript; null until first final result */
  final: string | null;
  /** True while actively streaming to Transcribe */
  isListening: boolean;
  /** Last error (getUserMedia denial, fetch failure, WebSocket error) */
  error: Error | null;
}

export function useTranscribe(opts: UseTranscribeOptions): UseTranscribeReturn {
  const { getIdToken, restApiUrl } = opts;

  const [isListening, setIsListening] = useState(false);
  const [partial, setPartial] = useState('');
  const [final, setFinal] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Refs for cleanup — not state because changes shouldn't trigger re-render
  const clientRef = useRef<TranscribeClient | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isListeningRef = useRef(false); // mirror for async auto-renewal

  // Cleanup on unmount (T-02-37)
  useEffect(() => {
    return () => {
      void stopInternal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopInternal = useCallback(async () => {
    isListeningRef.current = false;
    setIsListening(false);

    await clientRef.current?.stop().catch(() => { /* ignore */ });
    clientRef.current = null;

    // T-02-37: release mic indicator by stopping all tracks
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startInternal = useCallback(async (): Promise<void> => {
    setError(null);

    // 1. Fetch the presigned Transcribe URL (Cognito-authorized HTTPS)
    let presignedUrl: string;
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${restApiUrl}/transcribe-url`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!response.ok) {
        throw new Error(`GET /transcribe-url returned ${response.status}`);
      }
      const body = await response.json() as { url: string; expiresAt: number };
      presignedUrl = body.url;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      return;
    }

    // 2. Request mic access with optimal voice settings
    let mediaStream: MediaStream;
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      return;
    }

    streamRef.current = mediaStream;

    // 3. Create and start the Transcribe client
    const client = new TranscribeClient({ presignedUrl });
    clientRef.current = client;

    try {
      await client.start(
        mediaStream,
        // onPartial: update partial state and clear previous partial
        (text) => {
          setPartial(text);
        },
        // onFinal: save as final, clear partial
        (text) => {
          setFinal(text);
          setPartial('');
        },
      );
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      await stopInternal();
      return;
    }

    isListeningRef.current = true;
    setIsListening(true);
  }, [getIdToken, restApiUrl, stopInternal]);

  const start = useCallback(async () => {
    if (isListeningRef.current) return; // already listening
    await startInternal();
  }, [startInternal]);

  const stop = useCallback(async () => {
    await stopInternal();
    setPartial('');
  }, [stopInternal]);

  return {
    start,
    stop,
    partial,
    final,
    isListening,
    error,
  };
}
